import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi, systemApi } from '../services/api';
import { homeFor } from '../utils/corridors';
import { isPublicPath } from '../utils/publicPaths';

/**
 * Authentication Context
 * Manages user authentication state across the application
 */
const AuthContext = createContext(null);

/**
 * Custom hook to use authentication context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Wraps the app and provides authentication state and methods
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Check if user is already logged in on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }

    setLoading(false);
  }, []);

  // Corridors are assigned by the operator and can change while a participant is
  // signed in. The copy taken at sign-in is only a starting point: refresh it from
  // the engine on load and whenever the tab comes back, so a corridor added in the
  // console appears — and one removed disappears — without signing out.
  useEffect(() => {
    const refreshCorridors = async () => {
      if (!localStorage.getItem('token') || !localStorage.getItem('tenantId')) return;
      // A stale token would answer 401 and send a public page (activation link,
      // certificate check) to the sign-in page, losing its one-time token.
      if (isPublicPath()) return;
      try {
        const fresh = (await systemApi.getIdentity())?.corridors;
        if (!Array.isArray(fresh) || fresh.length === 0) return;
        setUser((current) => {
          if (!current || JSON.stringify(current.corridors || []) === JSON.stringify(fresh)) return current;
          const next = { ...current, corridors: fresh };
          localStorage.setItem('user', JSON.stringify(next));
          return next;
        });
      } catch {
        // Keep the session's copy; a 401 is handled by the API client.
      }
    };
    refreshCorridors();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshCorridors(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?.username]);

  /**
   * Login function
   * @param {string} username - User's username
   * @param {string} password - User's password
   * @returns {Promise<Object>} Login result
   */
  const login = async (username, password, tenantId) => {
    try {
      const response = await userApi.login(username, password, tenantId);

      if (response.success && response.user) {
        // Attach the participant's assigned corridors (default both for older sessions / operators).
        const userData = { ...response.user, corridors: response.corridors || ['FSPIOP', 'ISO20022'] };
        const token = response.token;

        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', token);

        // Store tenantId if present (MULTI_TENANT mode) — used by subsequent API calls
        if (response.tenantId) {
          localStorage.setItem('tenantId', response.tenantId);
        } else {
          localStorage.removeItem('tenantId');
        }

        // Land in an assigned corridor — a participant not on FSPIOP skips its dashboard.
        navigate(homeFor(userData));
        return { success: true, user: userData };
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /**
   * Logout function
   * Clears user data and redirects to login
   */
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('tenantId');
    navigate('/login');
  };

  /**
   * Check if user has specific role
   * @param {string} role - Role to check (ADMIN, OPERATOR, VIEWER)
   * @returns {boolean}
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  const isAdmin = () => {
    return user?.role === 'ADMIN';
  };

  /**
   * Check if user can write (ADMIN or OPERATOR)
   * @returns {boolean}
   */
  const canWrite = () => {
    return user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  };

  /**
   * Check if user must change password
   * @returns {boolean}
   */
  const mustChangePassword = () => {
    return user?.mustChangePassword === true;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    isAdmin,
    canWrite,
    mustChangePassword,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
